// Product page: pick a size, swap photos (click a small one to bring it up through the pixel effect), add to cart
// without leaving the page, keep the chosen size in the URL (?variant=…), and ← → / Esc for Previous, Next and back to
// the shop. Ported from the approved concept (concept/src/08-shop.js).
import { $, $$, session, setText, decode, depixelate, swapPhoto, motion, PIX_STEPS } from 'fx';
import { addToCart } from 'cart';
import { paintShip } from 'ship';

const page = $('[data-product]');
if (page) init();

function init() {
  const form = $('#p-form', page);
  const note = $('#p-note', page);
  const strings = JSON.parse(($('#product-strings') || {}).textContent || '{}');
  const addLabel = $('[data-add-label]', page);
  const addPrice = $('[data-add-price]', page);
  const main = $('#p-img', page);

  /* photos */
  if (main) depixelate(main);
  $$('.p-thumb img', page).forEach((t, i) => setTimeout(() => depixelate(t, PIX_STEPS, 90), 160 + i * 120));
  $$('.p-thumb', page).forEach((btn) => btn.addEventListener('click', () => {
    const thumb = $('img', btn);
    const was = { src: main.getAttribute('src'), srcset: main.getAttribute('srcset'), alt: main.alt, small: main.dataset.thumb };
    swapPhoto(main, btn.dataset.src, btn.dataset.srcset, btn.dataset.alt);
    main.dataset.thumb = thumb.getAttribute('src');
    btn.dataset.src = was.src;
    btn.dataset.srcset = was.srcset;
    btn.dataset.alt = was.alt;
    swapPhoto(thumb, was.small, '', '');
  }));

  /* size */
  const radios = $$('input[type="radio"][name="id"]', form);
  const select = $('select[name="id"]', form);
  // The size guide (sizes down the side) marks the row of the chosen size.
  const chart = $('[data-size-guide] .chart-table', page);
  function markSize(size) {
    if (!chart) return;
    const want = String(size || '').trim().toUpperCase();
    $$('tbody tr', chart).forEach((tr) => {
      const on = !!want && (tr.dataset.size || '').trim().toUpperCase() === want;
      [...tr.children].forEach((cell) => cell.classList.toggle('is-on', on));
    });
  }
  function picked(input) {
    if (input.dataset.price) addPrice.textContent = input.dataset.price;
    note.textContent = input.dataset.left
      ? strings.onlyLeft.replace('[n]', input.dataset.left).replace('[size]', input.dataset.size || '')
      : '';
    markSize(input.dataset.size || input.textContent);
    const url = new URL(location.href);
    url.searchParams.set('variant', input.value);
    history.replaceState(history.state, '', url);
  }
  radios.forEach((r) => r.addEventListener('change', () => picked(r)));
  if (select) select.addEventListener('change', () => picked(select.selectedOptions[0]));
  // Old links from the Hydrogen shop (?Size=M, ?Title=Default+Title, still in search results and posts): that size
  // is picked when it's in stock, and the old parameters leave the address.
  const old = new URLSearchParams(location.search);
  if (old.has('Size') || old.has('Title')) {
    const want = (old.get('Size') || '').trim().toUpperCase();
    const r = want && radios.find((x) => !x.disabled && (x.dataset.size || '').trim().toUpperCase() === want);
    if (r && !r.checked) { r.checked = true; picked(r); }
    const url = new URL(location.href);
    url.searchParams.delete('Size');
    url.searchParams.delete('Title');
    history.replaceState(history.state, '', url);
  }
  const checked = radios.find((r) => r.checked);
  if (checked && checked.dataset.price) addPrice.textContent = checked.dataset.price;
  if (checked) markSize(checked.dataset.size);

  /* Add to cart floats at the bottom of the screen on phones (theme.css) once a size is chosen (or right away when
     there's nothing to choose) and its own place, under the sizes, has been scrolled past. While that place is
     still further down, under the photos, it stays put, so it never covers the photos (the owner's call,
     1 Oct 2026). Picking a size while that place is still below the screen's edge scrolls it into full view. */
  const buy = $('.p-buy', form);
  const slot = $('.p-buy-slot', form);
  const header = $('#chrome');
  let chosen = !radios.length || !!checked;
  // Where the slot is. The fixed header covers the top of the screen, so the slot counts as passed once it's
  // under the header. Measured on every scroll (once a frame) rather than with an IntersectionObserver, which
  // misses a jump straight from below the slot to above it or back, like a tap on the iPhone's status bar.
  function where() {
    const top = header ? header.getBoundingClientRect().bottom : 0;
    const r = slot.getBoundingClientRect();
    return { past: r.bottom <= top, whole: r.top >= top && r.bottom <= innerHeight };
  }
  const float = () => { if (buy && slot) buy.classList.toggle('is-floating', chosen && where().past); };
  let queued = false;
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; float(); });
  };
  if (slot) {
    addEventListener('scroll', queue, { passive: true });
    addEventListener('resize', queue);
    float();
  }
  radios.forEach((r) => r.addEventListener('change', () => {
    chosen = true;
    float();
    if (!slot) return;
    const at = where();
    if (!at.whole && !at.past) {
      // Still (partly) below the screen or under the header: into full view, clear of the header (scrollIntoView
      // would put it under the header).
      const top = header ? header.getBoundingClientRect().bottom : 0;
      const r = slot.getBoundingClientRect();
      const by = r.top < top ? r.top - top - 12 : r.bottom - innerHeight + 12;
      scrollBy({ top: by, behavior: motion ? 'smooth' : 'auto' });
    }
  }));

  /* add to cart */
  form.addEventListener('invalid', (e) => {
    e.preventDefault();
    note.textContent = strings.pickSize;
  }, true);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (radios.length && !radios.some((r) => r.checked)) {
      note.textContent = strings.pickSize;
      const first = radios.find((r) => !r.disabled);
      if (first) first.focus();
      return;
    }
    const btn = $('[name="add"]', form);
    btn.disabled = true;
    try {
      await addToCart(new FormData(form), { open: false });
      updateShipHint();
      setText(addLabel, strings.added);
      decode(addLabel, { step: 18, hold: 120 });
      clearTimeout(addLabel._reset);
      addLabel._reset = setTimeout(() => { setText(addLabel, strings.add); decode(addLabel, { step: 18, hold: 120 }); }, 1600);
    } catch (err) {
      note.textContent = err.message || strings.error;
    } finally {
      btn.disabled = false;
    }
  });

  /* how far the cart is from free shipping: written by Liquid in the shop's currency, by ship.js in another one, and
     again after each Add to cart */
  const shipHint = $('[data-ship-hint]', page);
  const shipStrings = { from: strings.shipFrom, left: strings.shipLeft, done: strings.shipDone };
  if (shipHint) paintShip(shipHint, shipStrings, Number(shipHint.dataset.total) || 0, Number(shipHint.dataset.count) || 0);
  const shipFrom = $('[data-ship-from]', page);
  if (shipFrom) paintShip(shipFrom, shipStrings, 0, 0);
  async function updateShipHint() {
    if (!shipHint) return;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      paintShip(shipHint, shipStrings, cart.total_price, cart.item_count);
    } catch (e) { /* the line catches up on the next page */ }
  }

  /* "Tell me when it's back": the size picked there goes into the customer's tags */
  const restockSize = $('[data-restock-size]', page);
  const restockTags = $('[data-restock-tags]', page);
  if (restockSize && restockTags) {
    const setTags = () => {
      restockTags.value = `${restockTags.dataset.base},restock-${restockTags.dataset.handle}-${restockSize.value}`;
    };
    restockSize.addEventListener('change', setTags);
    setTags();
  }

  /* Previous / Next and "Back to shop" follow the list the visitor opened this product from (shop.js keeps it for the
     visit, with its filters and sort). Without one, or when this product isn't in it, the page's own links stay. */
  let came = null;
  try { came = JSON.parse(session.get('9ine-shop-list') || 'null'); } catch (e) { /* no list */ }
  const at = came && Array.isArray(came.items) ? came.items.indexOf(page.dataset.handle) : -1;
  if (at >= 0) {
    const n = came.items.length;
    const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const href = (i) => `${base}products/${came.items[(i + n) % n]}`;
    const pad = (v) => String(v).padStart(2, '0');
    const back = $('.p-top .go', page);
    if (back && came.url) back.href = came.url;
    const count = $('.p-index', page);
    if (count) count.textContent = `${pad(at + 1)}/${pad(n)}`;
    const foot = $('.p-foot', page);
    if (foot && n > 1) {
      $('[rel="prev"]', foot).href = href(at - 1);
      $('[rel="next"]', foot).href = href(at + 1);
    } else if (foot) {
      foot.hidden = true;
    }
  }

  /* ← → for Previous / Next, Esc for back to the shop (not while typing or choosing in a field) */
  document.addEventListener('keydown', (e) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || document.documentElement.classList.contains('is-locked')) return;
    if (e.target.closest && e.target.closest('input, textarea, select, [role="radiogroup"]')) return;
    const link = e.key === 'Escape' ? $('.p-top .go', page) : $(`.p-foot [data-key="${e.key}"]`, page);
    if (link) { e.preventDefault(); link.click(); }
  });
}
